import React from 'react';
import { Form } from 'react-bootstrap';
import InputGroup from 'react-bootstrap/InputGroup';
import { AuthSettings } from 'types/rest/mockServiceTypes';
import BlazeInputDropdown, { BlazeDropdownItem } from 'components/ui/dropdown/BlazeDropdown/BlazeInputDropdown';

interface AuthenticationTabProps {
  authSettings?: AuthSettings | null;
  disabled?: boolean;
  onUpdateAuthSettings: (value: AuthSettings | null | undefined) => void;
}

const AUTH_TYPE_OPTIONS = [
  { value: 'NONE', label: 'None' },
  { value: 'BASIC', label: 'Basic Authentication' },
  { value: 'HEADER', label: 'Header Authentication' },
];

const HEADER_MATCHER_OPTIONS = [
  { value: 'equals', label: 'Equals' },
  { value: 'contains', label: 'Contains' },
  { value: 'matches', label: 'Matches (regex)' },
  { value: 'equals_insensitive', label: 'Equals (case insensitive)' },
];

const defaultAuthTypeOption: BlazeDropdownItem = { id: 'NONE', name: 'None' };

const authTypeDropdownItems: BlazeDropdownItem[] = AUTH_TYPE_OPTIONS.filter(o => o.value !== 'NONE').map(o => ({
  id: o.value,
  name: o.label,
}));

const matcherDropdownItems: BlazeDropdownItem[] = HEADER_MATCHER_OPTIONS.map(o => ({
  id: o.value,
  name: o.label,
}));

const AuthenticationTab = (props: AuthenticationTabProps) => {
  const currentType = props.authSettings?.type ?? 'NONE';

  const onTypeChange = (newType: string) => {
    if (newType === 'NONE') {
      props.onUpdateAuthSettings(null);
    } else {
      props.onUpdateAuthSettings({ ...(props.authSettings ?? {}), type: newType });
    }
  };

  const onFieldChange = (field: keyof AuthSettings, value: string) => {
    const effectiveValue: string | undefined = field === 'password' && value === '' ? undefined : value;
    props.onUpdateAuthSettings({ ...(props.authSettings ?? {}), type: currentType, [field]: effectiveValue });
  };

  const selectedAuthTypeItem: BlazeDropdownItem =
    currentType === 'NONE'
      ? defaultAuthTypeOption
      : { id: currentType, name: AUTH_TYPE_OPTIONS.find(o => o.value === currentType)?.label ?? currentType };

  const currentMatcherName = props.authSettings?.matcherName ?? 'equals';
  const selectedMatcherItem: BlazeDropdownItem = {
    id: currentMatcherName,
    name: HEADER_MATCHER_OPTIONS.find(o => o.value === currentMatcherName)?.label ?? currentMatcherName,
  };

  return (
    <Form.Group>
      <Form.Label>Authentication Type</Form.Label>
      <div className="mb-3">
        <BlazeInputDropdown
          id="auth-type"
          showGroups={false}
          isSearchable={false}
          items={authTypeDropdownItems}
          selected={selectedAuthTypeItem}
          onClick={(item: BlazeDropdownItem) => onTypeChange(item.id as string)}
          isDefaultOptionSelectable={true}
          defaultOption={defaultAuthTypeOption}
          disabled={props.disabled}
          dropdownWidth="360px"
        />
      </div>

      {currentType === 'BASIC' && (
        <>
          <Form.Label htmlFor={'auth-username'}>Username</Form.Label>
          <InputGroup className="mb-3" style={{ width: '360px' }}>
            <Form.Control
              id={'auth-username'}
              type="text"
              value={props.authSettings?.username ?? ''}
              onChange={(e: any) => onFieldChange('username', e.target.value)}
              disabled={props.disabled}
              placeholder="Username"
            />
          </InputGroup>

          <Form.Label htmlFor={'auth-password'}>Password</Form.Label>
          <InputGroup className="mb-1" style={{ width: '360px' }}>
            <Form.Control
              id={'auth-password'}
              type="password"
              value={props.authSettings?.password ?? ''}
              onChange={(e: any) => onFieldChange('password', e.target.value)}
              disabled={props.disabled}
              placeholder="Leave blank to keep existing"
            />
          </InputGroup>
        </>
      )}

      {currentType === 'HEADER' && (
        <>
          <Form.Label htmlFor={'auth-header-name'}>Header Name</Form.Label>
          <InputGroup className="mb-3" style={{ width: '360px' }}>
            <Form.Control
              id={'auth-header-name'}
              type="text"
              value={props.authSettings?.headerName ?? ''}
              onChange={(e: any) => onFieldChange('headerName', e.target.value)}
              disabled={props.disabled}
              placeholder="e.g. X-API-Key"
            />
          </InputGroup>

          <Form.Label htmlFor={'auth-matcher-name'}>Match Strategy</Form.Label>
          <div className="mb-3">
            <BlazeInputDropdown
              id="auth-matcher-name"
              showGroups={false}
              isSearchable={false}
              items={matcherDropdownItems}
              selected={selectedMatcherItem}
              onClick={(item: BlazeDropdownItem) => onFieldChange('matcherName', item.id as string)}
              disabled={props.disabled}
              dropdownWidth="360px"
            />
          </div>

          <Form.Label htmlFor={'auth-matching-value'}>Header Value</Form.Label>
          <InputGroup className="mb-1" style={{ width: '360px' }}>
            <Form.Control
              id={'auth-matching-value'}
              type="text"
              value={props.authSettings?.matchingValue ?? ''}
              onChange={(e: any) => onFieldChange('matchingValue', e.target.value)}
              disabled={props.disabled}
              placeholder="Expected header value"
            />
          </InputGroup>
        </>
      )}
    </Form.Group>
  );
};

export default AuthenticationTab;
